/**
 * SERVICIO DE VERIFICACIÓN Y RESERVA DE STOCK
 * 
 * Este servicio maneja todo el flujo de:
 * 1. Verificar stock real contra el almacén
 * 2. Reservar stock cuando hay suficiente
 * 3. Calcular faltantes y enviar a Compras Pendientes
 * 4. Gestionar transacciones seguras
 */

import api from './api';
import { comprasPendientesService } from './api';
import { FLUJO_STOCK_CONFIG, isFeatureEnabled, stockLogger } from '../config/flujoStock.config';


/**
 * Verificar stock para todos los artículos de una cotización
 * @param {number} cotizacionId - ID de la cotización
 * @param {Array} articulos - Lista de artículos con { articulo_id, cantidad }
 * @returns {Promise<{stockSuficiente: boolean, articulosFaltantes: Array, articulosOK: Array}>}
 */
export const verificarStockCotizacion = async (cotizacionId, articulos) => {
  stockLogger.info('🔍 Verificando stock para cotización:', cotizacionId);
  
  // Si el feature flag está desactivado, simular que todo está OK
  if (!isFeatureEnabled('VALIDACION_STOCK_HABILITADA')) {
    stockLogger.debug('Feature flag desactivado, saltando verificación');
    return {
      stockSuficiente: true,
      articulosFaltantes: [],
      articulosOK: articulos.map(a => ({ ...a, stockDisponible: 999999 }))
    };
  }
  
  try {
    // Llamar al endpoint de verificación del backend
    const response = await api.post(FLUJO_STOCK_CONFIG.ENDPOINT_VERIFICAR_STOCK, {
      cotizacion_id: cotizacionId,
      articulos: articulos.map(art => ({
        articulo_id: art.articulo_id || art.id,
        cantidad_solicitada: art.cantidad
      }))
    });
    
    if (response.data.success) {
      stockLogger.info('✅ Verificación de stock completada:', response.data.data);
      return {
        stockSuficiente: response.data.data.stock_suficiente,
        articulosFaltantes: response.data.data.articulos_faltantes || [],
        articulosOK: response.data.data.articulos_ok || []
      };
    } else {
      throw new Error(response.data.message || 'Error en verificación de stock');
    }
    
  } catch (error) {
    stockLogger.error('❌ Error verificando stock:', error);
    throw error;
  }
};


/**
 * Verificación local usando datos que ya tenemos (fallback)
 * @param {Array} articulos - Lista de artículos
 * @returns {Object} Resultado de verificación
 */
const verificarStockLocal = (articulos) => {
  const articulosFaltantes = [];
  const articulosOK = [];
  
  for (const articulo of articulos) {
    // Usar el stock_disponible que ya viene en el artículo (del backend)
    const stockDisponible = articulo.stock_disponible || 0;
    const cantidadSolicitada = articulo.cantidad || 0;
    
    // ============================================================
    // CÁLCULO DE CANTIDAD FALTANTE
    // ============================================================
    // Ejemplo: Solicitan 7 unidades, tengo 5 en stock
    // cantidad_solicitada = 7
    // stock_disponible = 5
    // cantidad_faltante = 7 - 5 = 2 (solo comprar 2, no las 7)
    // ============================================================
    
    if (cantidadSolicitada > stockDisponible) {
      const cantidadFaltante = cantidadSolicitada - stockDisponible;
      
      stockLogger.info(`📊 Stock insuficiente: ${articulo.nombre || articulo.articulo_nombre}`);
      stockLogger.info(`   Solicitado: ${cantidadSolicitada} | Disponible: ${stockDisponible} | Faltante: ${cantidadFaltante}`);
      stockLogger.info(`   → Se sugiere comprar: ${Math.ceil(cantidadFaltante * FLUJO_STOCK_CONFIG.FACTOR_COMPRA)} unidades`);
      
      articulosFaltantes.push({
        articulo_id: articulo.articulo_id || articulo.id,
        articulo_nombre: articulo.nombre || articulo.articulo_nombre,
        cantidad_solicitada: cantidadSolicitada,
        stock_disponible: stockDisponible,
        cantidad_faltante: cantidadFaltante,
        cantidad_a_comprar: Math.ceil(cantidadFaltante * FLUJO_STOCK_CONFIG.FACTOR_COMPRA),
        url_proveedor: articulo.link_proveedor || articulo.url_proveedor,
        precio_referencia: articulo.precio_costo || articulo.precio_unitario || 0,
        categoria_id: articulo.categoria_id,
        prioridad: FLUJO_STOCK_CONFIG.PRIORIDAD_DEFAULT,
        origen: 'COTIZACION',
        cotizacion_id: articulo.cotizacion_id,
        observaciones: `Solicitud automática desde Cotización #${articulo.cotizacion_id}`
      });
    } else {
      // ============================================================
      // STOCK SUFICIENTE - Se reserva todo lo solicitado
      // ============================================================
      // Ejemplo: Solicitan 3 unidades, tengo 10 en stock
      // → Reservar 3, quedan 7 disponibles para otras cotizaciones
      // ============================================================
      
      stockLogger.info(`✅ Stock OK: ${articulo.nombre || articulo.articulo_nombre}`);
      stockLogger.info(`   Solicitado: ${cantidadSolicitada} | Disponible: ${stockDisponible} | Reservar: ${cantidadSolicitada}`);
      
      articulosOK.push({
        ...articulo,
        stockDisponible,
        cantidadReservar: cantidadSolicitada
      });
    }
  }
  
  // ============================================================
  // RESUMEN DE VERIFICACIÓN
  // ============================================================
  const totalArticulos = articulos.length;
  const conStock = articulosOK.length;
  const sinStock = articulosFaltantes.length;
  
  if (sinStock > 0) {
    stockLogger.info(`⚠️ RESUMEN: ${sinStock} de ${totalArticulos} artículos necesitan compra`);
    stockLogger.info(`   Artículos con stock suficiente: ${conStock}`);
    stockLogger.info(`   Artículos faltantes (comprar): ${sinStock}`);
    articulosFaltantes.forEach(art => {
      stockLogger.info(`   - ${art.articulo_nombre}: comprar ${art.cantidad_a_comprar} (faltan ${art.cantidad_faltante})`);
    });
  } else {
    stockLogger.info(`✅ RESUMEN: Todos los artículos (${totalArticulos}) tienen stock suficiente`);
  }
  // ============================================================
  
  return {
    stockSuficiente: articulosFaltantes.length === 0,
    articulosFaltantes,
    articulosOK
  };
};


/**
 * Reservar stock para los artículos de una cotización
 * @param {number} cotizacionId - ID de la cotización
 * @param {Array} articulos - Artículos a reservar (con cantidad)
 * @returns {Promise<boolean>}
 */
export const reservarStockCotizacion = async (cotizacionId, articulos) => {
  stockLogger.info('🔒 Reservando stock para cotización:', cotizacionId);
  
  if (!isFeatureEnabled('RESERVA_STOCK_HABILITADA')) {
    stockLogger.debug('Reserva desactivada, simulando éxito');
    return true;
  }
  
  try {
    const response = await api.post(
      FLUJO_STOCK_CONFIG.ENDPOINT_RESERVAR_STOCK(cotizacionId),
      {
        articulos: articulos.map(art => ({
          articulo_id: art.articulo_id || art.id,
          cantidad: art.cantidadReservar || art.cantidad
        }))
      }
    );
    
    if (response.data.success) {
      stockLogger.info('✅ Stock reservado correctamente');
      return true;
    } else {
      throw new Error(response.data.message || 'Error al reservar stock');
    }
    
  } catch (error) {
    stockLogger.error('❌ Error reservando stock:', error);
    return false;
  }
};


/**
 * Revertir reservas de stock (al editar cotización)
 * @param {number} cotizacionId - ID de la cotización
 * @returns {Promise<boolean>}
 */
export const revertirReservasCotizacion = async (cotizacionId) => {
  stockLogger.info('↩️ Revirtiendo reservas para cotización:', cotizacionId);
  
  if (!isFeatureEnabled('RESERVA_STOCK_HABILITADA')) {
    return true;
  }
  
  try {
    const response = await api.post(
      FLUJO_STOCK_CONFIG.ENDPOINT_REVERTIR_RESERVAS(cotizacionId)
    );
    
    if (response.data.success) {
      stockLogger.info('✅ Reservas revertidas');
      return true;
    } else {
      throw new Error(response.data.message || 'Error al revertir reservas');
    }
    
  } catch (error) {
    stockLogger.error('❌ Error revirtiendo reservas:', error);
    return false;
  }
};


/**
 * Enviar artículos faltantes a Compras Pendientes
 * @param {number} cotizacionId - ID de la cotización
 * @param {string} cotizacionNumero - Número de cotización
 * @param {string} clienteNombre - Nombre del cliente
 * @param {Array} articulosFaltantes - Lista de artículos sin stock
 * @returns {Promise<{enviados: number, errores: Array}>}
 */
export const enviarFaltantesAComprasPendientes = async (
  cotizacionId,
  cotizacionNumero,
  clienteNombre,
  empresaNombre,
  articulosFaltantes
) => {
  stockLogger.info('📤 Enviando faltantes a Compras Pendientes:', articulosFaltantes.length);
  
  if (!isFeatureEnabled('ENVIO_COMPRAS_PENDIENTES_HABILITADO')) {
    stockLogger.debug('Envío a Compras Pendientes desactivado');
    return { enviados: 0, errores: [] };
  }
  
  const resultados = {
    enviados: 0,
    errores: []
  };
  
  // Usar el servicio batch para enviar todos de una vez
  try {
    const response = await comprasPendientesService.batchDesdeCotizacion(
      cotizacionId,
      cotizacionNumero,
      clienteNombre,
      empresaNombre,
      articulosFaltantes.map(art => ({
        articulo_id: art.articulo_id,
        nombre: art.articulo_nombre,
        cantidad: art.cantidad_faltante,
        descripcion: art.observaciones,
        via_compra: art.tipo || 'nacional',
        link_proveedor: art.url_proveedor
      }))
    );
    
    if (response.success) {
      resultados.enviados = articulosFaltantes.length;
      stockLogger.info('✅ Enviados a Compras Pendientes:', resultados.enviados);
    } else {
      throw new Error(response.message || 'Error al enviar');
    }
    
  } catch (error) {
    stockLogger.error('❌ Error enviando a Compras Pendientes:', error);
    resultados.errores.push(error.message);
  }
  
  return resultados;
};


/**
 * Procesar cotización completa (función principal)
 * @param {Object} params - Parámetros del procesamiento
 * @returns {Promise<{exito: boolean, stockSuficiente?: boolean, error?: string}>}
 */
export const procesarCotizacionConStock = async ({
  cotizacionId,
  cotizacionNumero,
  clienteNombre,
  empresaNombre,
  articulos,
  accion // 'crear' | 'editar' | 'aceptar'
}) => {
  stockLogger.info('🚀 Procesando cotización con stock:', { cotizacionId, accion });
  
  // Si el flujo está desactivado, simular éxito
  if (!isFeatureEnabled('VALIDACION_STOCK_HABILITADA')) {
    stockLogger.debug('Flujo de stock desactivado, continuando normal');
    return { exito: true, stockSuficiente: true };
  }
  
  try {
    // 1. Si es edición, revertir reservas anteriores
    if (accion === 'editar') {
      await revertirReservasCotizacion(cotizacionId);
    }
    
    // 2. Verificar stock para todos los artículos
    const verificacion = await verificarStockCotizacion(cotizacionId, articulos);
    
    // 3. Si hay faltantes, enviar a Compras Pendientes
    if (!verificacion.stockSuficiente && verificacion.articulosFaltantes.length > 0) {
      stockLogger.warn('⚠️ Stock insuficiente detectado');
      
      const envioResult = await enviarFaltantesAComprasPendientes(
        cotizacionId,
        cotizacionNumero,
        clienteNombre,
        empresaNombre,
        verificacion.articulosFaltantes
      );
      
      // Si falló el envío y tenemos rollback habilitado, abortar
      if (envioResult.errores.length > 0 && isFeatureEnabled('ROLLBACK_TRANSACCIONAL_HABILITADO')) {
        throw new Error(FLUJO_STOCK_CONFIG.MENSAJES.ERROR_COMPRAS_PENDIENTES);
      }
      
      return {
        exito: true,
        stockSuficiente: false,
        articulosFaltantes: verificacion.articulosFaltantes,
        mensaje: FLUJO_STOCK_CONFIG.MENSAJES.STOCK_INSUFICIENTE
      };
    }
    
    // 4. Si hay stock suficiente, reservar
    if (verificacion.stockSuficiente && verificacion.articulosOK.length > 0) {
      const reservado = await reservarStockCotizacion(cotizacionId, verificacion.articulosOK);
      
      if (!reservado && isFeatureEnabled('ROLLBACK_TRANSACCIONAL_HABILITADO')) {
        throw new Error(FLUJO_STOCK_CONFIG.MENSAJES.ERROR_RESERVA);
      }
      
      return {
        exito: true,
        stockSuficiente: true,
        mensaje: FLUJO_STOCK_CONFIG.MENSAJES.STOCK_SUFICIENTE
      };
    }
    
    // Si no hay artículos que procesar
    return { exito: true, stockSuficiente: true };
    
  } catch (error) {
    stockLogger.error('❌ Error procesando cotización:', error);
    return {
      exito: false,
      error: error.message || 'Error desconocido al procesar cotización'
    };
  }
};





// ============================================================================
// EXPORTACIÓN DEL SERVICIO
// ============================================================================

const stockVerificacionService = {
  verificarStockCotizacion,
  reservarStockCotizacion,
  revertirReservasCotizacion,
  enviarFaltantesAComprasPendientes,
  procesarCotizacionConStock,
  verificarStockLocal
};

export default stockVerificacionService;
